import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';

const IS_DEV_MODE = process.env.NODE_ENV === 'development';

const toastIn = keyframes`
  from { transform: translateX(-24px) translateY(8px); opacity: 0; }
  to   { transform: translateX(0) translateY(0); opacity: 1; }
`;
const toastOut = keyframes`
  from { transform: translateX(0); opacity: 1; }
  to   { transform: translateX(-20px); opacity: 0; }
`;
const panelUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const ToastContainer = styled.div`
  position: fixed;
  bottom: 80px;
  left: 24px;
  right: 24px;
  z-index: 8000;
  display: flex;
  flex-direction: column-reverse;
  gap: 10px;
  pointer-events: none;
`;

const ToastEl = styled.div`
  pointer-events: all;
  max-width: 480px;
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  border: 1px solid;
  animation: ${p => p.$hiding ? toastOut : toastIn} ${p => p.$hiding ? '0.25s' : '0.35s'} cubic-bezier(0.34,1.56,0.64,1) forwards;
  background: ${p => p.$type === 'success' ? '#f0fdf4' : p.$type === 'info' ? '#eff6ff' : '#fffbeb'};
  border-color: ${p => p.$type === 'success' ? 'rgba(34,197,94,0.2)' : p.$type === 'info' ? 'rgba(59,130,246,0.2)' : 'rgba(245,158,11,0.2)'};
`;
const ToastIcon = styled.div`
  width: 32px; height: 32px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  background: ${p => p.$type === 'success' ? 'rgba(34,197,94,0.1)' : p.$type === 'info' ? 'rgba(59,130,246,0.1)' : 'rgba(245,158,11,0.1)'};
  color: ${p => p.$type === 'success' ? '#16a34a' : p.$type === 'info' ? '#2563eb' : '#d97706'};
`;
const ToastContent = styled.div`flex: 1; min-width: 0; word-break: break-word;`;
const ToastTitle = styled.div`
  font-size: 0.875rem; font-weight: 600; margin-bottom: 0.2rem;
  color: ${p => p.$type === 'success' ? '#166534' : p.$type === 'info' ? '#1e40af' : '#92400e'};
`;
const ToastMsg = styled.div`
  font-size: 0.82rem; line-height: 1.5;
  color: ${p => p.$type === 'success' ? 'rgba(22,101,52,0.75)' : p.$type === 'info' ? 'rgba(30,64,175,0.75)' : 'rgba(146,64,14,0.75)'};
`;
const ToastClose = styled.button`
  width: 24px; height: 24px; border-radius: 4px; background: none; border: none;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  opacity: 0.4; flex-shrink: 0; align-self: flex-start; margin-top: 2px; transition: opacity 0.2s;
  &:hover { opacity: 0.8; }
`;
const BellWrap = styled.div`
  position: fixed; bottom: 24px; left: 24px; z-index: 8500;
`;
const BellBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 48px; height: 48px; border-radius: 50%;
  background: #1a1a1a; border: 1px solid rgba(255,255,255,0.1);
  box-shadow: 0 4px 16px rgba(0,0,0,0.35); cursor: pointer;
  color: rgba(255,255,255,0.7); transition: all 0.2s; position: relative;
  &:hover { background: #222; color: #fff; transform: translateY(-1px); }
`;
const BellBadge = styled.span`
  position: absolute; top: -4px; right: -4px;
  min-width: 16px; height: 16px; border-radius: 8px;
  background: #e97024; color: #fff; font-size: 0.58rem; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  border: 2px solid #1a1a1a; padding: 0 3px;
`;
const Panel = styled.div`
  position: absolute; bottom: calc(100% + 12px); left: 0; width: 480px;
  background: #fff; border: 1px solid rgba(0,0,0,0.08); border-radius: 16px;
  box-shadow: 0 -4px 24px rgba(0,0,0,0.1), 0 8px 32px rgba(0,0,0,0.12);
  overflow: hidden; animation: ${panelUp} 0.2s ease;
  font-family: 'Montserrat', system-ui, sans-serif;
`;
const PanelHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.875rem 1rem 0.625rem; border-bottom: 1px solid rgba(0,0,0,0.07);
`;
const PanelTitle = styled.span`
  font-size: 0.72rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.5px; color: #9ca3af;
`;
const MarkAllBtn = styled.button`
  font-size: 0.72rem; color: #e97024; background: none; border: none;
  cursor: pointer; font-weight: 500; font-family: inherit;
  &:hover { opacity: 0.7; }
`;
const NotifList = styled.div`
  padding: 0.625rem; display: flex; flex-direction: column; gap: 0.5rem;
  max-height: 420px; overflow-y: auto;
`;
const NotifCard = styled.div`
  border-radius: 8px; padding: 0.875rem; border: 1px solid; position: relative;
  background: ${p => p.$type === 'success' ? '#f0fdf4' : p.$type === 'info' ? '#eff6ff' : '#fffbeb'};
  border-color: ${p => p.$type === 'success' ? 'rgba(34,197,94,0.2)' : p.$type === 'info' ? 'rgba(59,130,246,0.2)' : 'rgba(245,158,11,0.2)'};
  ${p => p.$unread && `
    &::before {
      content: '';
      position: absolute; left: -1px; top: 12px; bottom: 12px;
      width: 3px; border-radius: 0 2px 2px 0; background: #000;
    }
  `}
`;
const NotifTop = styled.div`
  display: flex; align-items: flex-start; gap: 0.625rem; margin-bottom: 0.4rem;
`;
const NotifIcon = styled.div`
  width: 26px; height: 26px; border-radius: 6px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  background: ${p => p.$type === 'success' ? 'rgba(34,197,94,0.1)' : p.$type === 'info' ? 'rgba(59,130,246,0.1)' : 'rgba(245,158,11,0.1)'};
  color: ${p => p.$type === 'success' ? '#16a34a' : p.$type === 'info' ? '#2563eb' : '#d97706'};
`;
const NotifTitleRow = styled.div`flex: 1;`;
const NotifTitle = styled.div`
  font-size: 0.8rem; font-weight: 600;
  color: ${p => p.$type === 'success' ? '#166534' : p.$type === 'info' ? '#1e40af' : '#92400e'};
`;
const NotifTime = styled.div`font-size: 0.67rem; color: #9ca3af; margin-top: 0.15rem;`;
const NotifDismiss = styled.button`
  width: 20px; height: 20px; border-radius: 4px; background: none; border: none;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  color: #9ca3af; flex-shrink: 0; transition: all 0.2s;
  &:hover { color: #374151; background: rgba(0,0,0,0.05); }
`;
const NotifMsg = styled.div`
  font-size: 0.75rem; line-height: 1.45;
  color: ${p => p.$type === 'success' ? 'rgba(22,101,52,0.75)' : p.$type === 'info' ? 'rgba(30,64,175,0.75)' : 'rgba(146,64,14,0.75)'};
`;
const PanelEmpty = styled.div`
  padding: 1.5rem 1rem; text-align: center; font-size: 0.8rem; color: #9ca3af;
`;
const ActionBtn = styled.button`
  margin-top: 0.6rem; width: 100%;
  padding: 0.5rem 0.75rem; border-radius: 6px; border: none; cursor: pointer;
  background: #e97024; color: #fff; font-size: 0.78rem; font-weight: 600;
  font-family: inherit; transition: background 0.2s;
  &:hover { background: #d4621a; }
`;

// ─── Status pill for PENDING notifications (fallback when no job progress) ──
const StatusPill = styled.span`
  display: inline-flex; align-items: center; gap: 0.25rem;
  padding: 0.1rem 0.45rem; border-radius: 10px;
  font-size: 0.68rem; font-weight: 600; margin-top: 0.3rem;
  background: rgba(59,130,246,0.1); color: #1e40af;
`;

const PulsingDot = styled.span`
  width: 6px; height: 6px; border-radius: 50%;
  background: #2563eb; display: inline-block;
  animation: pulse 1.4s ease-in-out infinite;
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
`;

const CheckIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
  </svg>
);
const ClockIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
  </svg>
);
const WarnIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
  </svg>
);
const XIcon = () => (
  <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
  </svg>
);
const BellIcon = () => (
  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
  </svg>
);

function formatTime(isoString) {
  if (!isoString) return 'Just now';
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function getIndexingDocumentName(notification, progress, meta) {
  const total = Number(progress?.totalDocuments) || meta?.documents?.length || 0;
  const done = Number(progress?.completedDocuments);
  let label;
  if (total > 0) {
    label = Number.isFinite(done) && done > 0
      ? `${total} document${total !== 1 ? 's' : ''} (${done}/${total})`
      : `${total} document${total !== 1 ? 's' : ''}`;
  } else {
    const match = notification.message?.match(/^(\d+)\s+document/i);
    const n = match ? Number(match[1]) : null;
    label = n ? `${n} document${n !== 1 ? 's' : ''}` : 'Documents';
  }
  return meta?.folderName ? `${meta.folderName} · ${label}` : label;
}


export default function NotificationSystem({
  notifications,
  toasts,
  unreadCount,
  jobProgress = {},
  jobMeta = {},
  dismissToast,
  dismissNotif,
  markAllRead,
  onAction,
}) {
  const [panelOpen, setPanelOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setPanelOpen(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const iconMap = {
    success: <CheckIcon />,
    info: <ClockIcon />,
    warning: <WarnIcon />,
  };

  return (
    <>
      <ToastContainer>
        {toasts.map(t => (
          <ToastEl key={t.id} $type={t.type} $hiding={t.hiding}>
            <ToastIcon $type={t.type}>{iconMap[t.type]}</ToastIcon>
            <ToastContent>
              <ToastTitle $type={t.type}>{t.title}</ToastTitle>
              <ToastMsg $type={t.type}>{t.message}</ToastMsg>
            </ToastContent>
            <ToastClose onClick={() => dismissToast(t.id)}><XIcon /></ToastClose>
          </ToastEl>
        ))}
      </ToastContainer>

      <BellWrap ref={wrapRef}>
        <BellBtn onClick={() => setPanelOpen(v => !v)} title="Notifications">
          <BellIcon />
          {unreadCount > 0 && <BellBadge>{unreadCount > 99 ? '99+' : unreadCount}</BellBadge>}
        </BellBtn>

        {panelOpen && (
          <Panel>
            <PanelHeader>
              <PanelTitle>Notifications</PanelTitle>
              {notifications.length > 0 && (
                <MarkAllBtn onClick={markAllRead}>Mark all read</MarkAllBtn>
              )}
            </PanelHeader>
            <NotifList>
              {notifications.length === 0 && (
                <PanelEmpty>No notifications yet</PanelEmpty>
              )}
              {notifications.map(n => {
                const progress = n.jobId ? jobProgress[n.jobId] : null;
                const meta = n.jobId ? jobMeta[n.jobId] : null;
                const isIndexingPending = n.status === 'PENDING' && n.jobId;

                return (
                <NotifCard key={n.notificationId} $type={n.type} $unread={n.unread}>
                  <NotifTop>
                    <NotifIcon $type={n.type}>{iconMap[n.type]}</NotifIcon>
                    <NotifTitleRow>
                      <NotifTitle $type={n.type}>{n.title}</NotifTitle>
                      <NotifTime>{formatTime(n.createdAt)}</NotifTime>
                    </NotifTitleRow>
                    {/* Action-required notifications cannot be dismissed — they are
                        resolved only by completing the action (see ActionBtn below).
                        Exception: continue_extraction has no action button outside dev
                        (see IS_DEV_MODE below), so it must stay dismissable there or it
                        would be permanently stuck. */}
                    {(!n.requiresAction || (n.actionType === 'continue_extraction' && !IS_DEV_MODE)) && (
                      <NotifDismiss onClick={() => dismissNotif(n.notificationId)}>
                        <XIcon />
                      </NotifDismiss>
                    )}
                  </NotifTop>
                  {isIndexingPending ? (
                    <>
                      <NotifMsg $type={n.type}>
                        {getIndexingDocumentName(n, progress, meta) || 'Folder'} — indexing in progress
                        {progress?.completedDocuments != null && progress?.totalDocuments != null
                          ? ` (${progress.completedDocuments} of ${progress.totalDocuments} docs)`
                          : ''}
                      </NotifMsg>
                      <StatusPill><PulsingDot /> Indexing in progress…</StatusPill>
                    </>
                  ) : (
                    <>
                      <NotifMsg $type={n.type}>{n.message}</NotifMsg>
                      {n.requiresAction && n.actionType === 'zone_confirm' && (
                        <ActionBtn
                          onClick={() => { onAction && onAction(n); setPanelOpen(false); }}
                        >
                          Confirm zone setup
                        </ActionBtn>
                      )}
                      {IS_DEV_MODE && n.requiresAction && n.actionType === 'continue_extraction' && (
                        <ActionBtn
                          onClick={() => { onAction && onAction(n); setPanelOpen(false); }}
                        >
                          Continue Extraction
                        </ActionBtn>
                      )}
                      {n.requiresAction && n.actionType === 'reconcile' && (
                        <ActionBtn
                          onClick={() => { onAction && onAction(n); setPanelOpen(false); }}
                        >
                          Review deliverables
                        </ActionBtn>
                      )}
                    </>
                  )}
                </NotifCard>
              );
              })}
            </NotifList>
          </Panel>
        )}
      </BellWrap>
    </>
  );
}